import React, { useState, useEffect } from 'react';
import UserDashboardLayout from '../../components/dashboard/UserDashboardLayout';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { motion, AnimatePresence } from 'framer-motion';

interface LeadClassifiedPackage {
  _id: string;
  name: string;
  price: string;
  duration: string;
  users: string;
  leadAccess: string;
  dailyUnlock: string;
}

export default function BrokerBuyLeadPlan() {
  const { token } = useAuth();
  const { theme } = useTheme();
  const [packages, setPackages] = useState<LeadClassifiedPackage[]>([]);
  const [adminUpiId, setAdminUpiId] = useState('');
  const [loading, setLoading] = useState(true);

  // Modal / Checkout state
  const [selectedPkg, setSelectedPkg] = useState<LeadClassifiedPackage | null>(null);
  const [transactionId, setTransactionId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!token) return;
        setLoading(true);

        const [pkgsData, settingsData] = await Promise.all([
          api.getLeadClassifiedPackages(token),
          api.getSystemSettings()
        ]);

        setPackages(Array.isArray(pkgsData) ? pkgsData : []);
        setAdminUpiId(settingsData?.adminUpiId || 'admin@upi');
      } catch (err) {
        toast.error('Failed to load packages or payment settings');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const openCheckout = (pkg: LeadClassifiedPackage) => {
    setSelectedPkg(pkg);
    setTransactionId('');
  };

  const closeCheckout = () => {
    setSelectedPkg(null);
    setTransactionId('');
  };

  const handlePurchase = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token || !selectedPkg) return;
    if (!transactionId.trim()) {
      toast.error('Please enter a valid transaction reference number');
      return;
    }

    setSubmitting(true);
    try {
      await api.purchasePlan(token, {
        packageId: selectedPkg._id,
        name: selectedPkg.name,
        price: selectedPkg.price,
        duration: selectedPkg.duration,
        users: selectedPkg.users,
        leadAccess: selectedPkg.leadAccess,
        dailyUnlock: selectedPkg.dailyUnlock,
        transactionId
      });
      toast.success('Purchase submitted successfully! Admin will review your transaction.');
      closeCheckout();
    } catch (err) {
      toast.error('Failed to submit purchase request');
    } finally {
      setSubmitting(false);
    }
  };

  const extractAmount = (priceStr: string) => {
    const matches = priceStr.match(/\d+/g);
    return matches ? matches.join('') : '';
  };

  return (
    <UserDashboardLayout>
      <div className="mb-10">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-10 p-2 md:p-0">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-2 leading-none">Buy Lead Plans</h1>
            <p className="text-slate-500 font-medium text-sm md:text-base">Purchase specialized lead plans to boost your business.</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="px-5 py-2.5 bg-emerald-50 text-emerald-600 rounded-md border border-emerald-100 flex items-center gap-2 shadow-sm font-bold text-xs">
              <i className="fa-solid fa-shield-check text-emerald-500"></i>
              Secure Payment
            </div>
          </div>
        </div>

        {/* Packages Grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 bg-white dark:bg-slate-900 rounded-md border border-gray-100 dark:border-slate-800 shadow-sm mx-2 md:mx-0">
            <div className="w-12 h-12 border-4 border-[#05144b] border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="font-bold text-[#05144b] text-[14px]">Loading packages...</p>
          </div>
        ) : packages.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-md p-16 text-center border border-gray-100 dark:border-slate-800 shadow-sm mx-2 md:mx-0">
            <div className="w-20 h-20 bg-slate-50 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="fa-solid fa-box-open text-3xl text-slate-300 dark:text-slate-600"></i>
            </div>
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">No Plans Available</h2>
            <p className="text-slate-500 mb-6 font-medium text-sm">Please check back later or contact support for available plans.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-2 md:px-0">
            {packages.map((pkg, idx) => (
              <motion.div
                key={pkg._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.1 }}
                className="bg-white dark:bg-slate-900 rounded-xl p-8 border border-gray-100 dark:border-slate-800 shadow-sm hover:shadow-lg transition-all relative overflow-hidden group flex flex-col"
              >
                {/* Background Accent */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-600/5 rounded-bl-full -mr-8 -mt-8 group-hover:bg-blue-600/10 transition-colors duration-500"></div>
                
                <div className="flex justify-between items-start mb-6 relative z-10">
                  <div className="w-14 h-14 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-xl">
                    <i className="fa-solid fa-gem"></i>
                  </div>
                  <span className="px-3 py-1 bg-[#05144b] text-white rounded-md font-bold text-xs">Premium</span>
                </div>

                <div className="relative z-10 flex-grow">
                  <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1 group-hover:text-blue-600 transition-colors">{pkg.name}</h3>
                  <div className="flex items-baseline gap-1 mb-8">
                    <span className="text-4xl font-black text-[#05144b]">₹{pkg.price}</span>
                    <span className="text-sm font-medium text-slate-500">/ {pkg.duration}</span>
                  </div>

                  <div className="space-y-4 mb-8 pt-6 border-t border-gray-100 dark:border-slate-800">
                    {[
                      { icon: 'fa-user-group', text: `${pkg.users} Users Allowed` },
                      { icon: 'fa-unlock-keyhole', text: `Lead Access: ${pkg.leadAccess}` },
                      { icon: 'fa-bolt-lightning', text: `Daily Unlock: ${pkg.dailyUnlock}` }
                    ].map((feat, i) => (
                      <div key={i} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center text-xs">
                          <i className={`fa-solid ${feat.icon}`}></i>
                        </div>
                        <span className="text-sm font-medium text-slate-600 dark:text-slate-300">{feat.text}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="mt-auto">
                  <button
                    onClick={() => openCheckout(pkg)}
                    className="w-full py-3.5 bg-[#05144b] text-white text-sm font-bold rounded-md hover:bg-[#0a1e66] transition-all shadow-md flex items-center justify-center gap-2 active:scale-95"
                  >
                    Purchase Plan <i className="fa-solid fa-arrow-right text-xs"></i>
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Checkout & QR Payment Modal */}
      <AnimatePresence>
        {selectedPkg && (
          <div className="fixed inset-0 z-[2000] flex items-center justify-center px-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeCheckout}
              className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-2xl bg-white dark:bg-slate-900 rounded-xl p-6 shadow-2xl overflow-hidden"
            >
              <div className="flex justify-between items-center w-full mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-slate-900 dark:text-white">Secure Payment</h2>
                  <p className="text-sm text-slate-500 mt-1">Pay via UPI to complete purchase</p>
                </div>
                <button
                  onClick={closeCheckout}
                  className="w-10 h-10 rounded-md bg-slate-100 dark:bg-slate-800 text-slate-500 hover:bg-rose-100 hover:text-rose-600 transition-all font-bold flex items-center justify-center"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>
              </div>

              <div className="flex flex-col md:flex-row gap-6">
                {/* QR Code Section */}
                <div className="md:w-1/2 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 rounded-xl p-5 flex flex-col items-center text-center">
                  <p className="text-sm font-bold text-slate-700 dark:text-slate-300 mb-2">Scan QR Code to Pay</p>
                  <div className="text-xs font-medium text-slate-500 mb-4 bg-white dark:bg-slate-800 px-4 py-1.5 rounded-md border border-slate-200 dark:border-slate-700 shadow-sm select-all">
                    UPI ID: {adminUpiId}
                  </div>

                  <div className="w-36 h-36 bg-white p-2 border border-slate-200 rounded-xl shadow-sm mb-4 flex items-center justify-center">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?size=130x130&data=${encodeURIComponent(
                        `upi://pay?pa=${adminUpiId}&pn=My%20Property%20Guru&am=${extractAmount(
                          selectedPkg.price
                        )}&cu=INR`
                      )}`}
                      alt="UPI QR Code"
                      className="w-full h-full object-contain"
                    />
                  </div>

                  <div className="flex items-center justify-center gap-2 mt-auto">
                    <span className="text-sm font-medium text-slate-500">Amount to pay:</span>
                    <span className="text-2xl font-black text-[#05144b]">₹{extractAmount(selectedPkg.price)}</span>
                  </div>
                </div>

                {/* Form Section */}
                <div className="md:w-1/2 flex flex-col justify-center">
                  <form onSubmit={handlePurchase} className="w-full space-y-4">
                    <div className="space-y-2">
                      <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wide">Transaction ID (UTR)</label>
                      <input
                        type="text"
                        required
                        value={transactionId}
                        onChange={(e) => setTransactionId(e.target.value)}
                        placeholder="Enter 12-digit UTR Number"
                        className="w-full px-4 py-3 rounded-md border border-slate-300 dark:border-slate-700 focus:border-[#05144b] focus:ring-1 focus:ring-[#05144b] outline-none bg-white dark:bg-slate-800 text-slate-900 dark:text-white font-medium text-sm transition-all shadow-sm"
                      />
                      <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">After successful payment via the QR code or UPI ID, please enter the 12-digit transaction reference number (UTR) to authenticate your payment.</p>
                    </div>

                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={closeCheckout}
                        className="flex-1 py-3.5 rounded-md bg-slate-100 text-slate-600 hover:bg-slate-200 font-bold text-sm transition-all"
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        disabled={submitting}
                        className="flex-1 py-3.5 rounded-md bg-[#05144b] text-white hover:bg-[#0a1e66] font-bold text-sm transition-all shadow-md flex items-center justify-center gap-2 active:scale-95 disabled:opacity-70"
                      >
                        {submitting ? <><i className="fa-solid fa-circle-notch fa-spin"></i> Submitting...</> : 'Submit Payment'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </UserDashboardLayout>
  );
}
